import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box, Button, TextField, Typography, Paper, CircularProgress, Alert,
    Stack, Table, TableHead, TableBody, TableRow, TableCell,
    FormControl, Select, MenuItem, IconButton
} from '@mui/material';
import * as api from './volumeDiscountsApi';
import type { VolumeDiscount, QuantityGroup, VolumeDiscountLimit } from './volumeDiscountsApi';
import { sx14, paperSx, FormRow, DetailHeader } from '../common/detailView';

interface Props {
    volumeDiscountId: number | null;
}

export function VolumeDiscountView({ volumeDiscountId }: Props) {
    const { t } = useTranslation();

    const [item, setItem] = useState<VolumeDiscount | null>(null);
    const [quantityGroups, setQuantityGroups] = useState<QuantityGroup[]>([]);
    const [isEditing, setIsEditing] = useState(false);
    const [isNew, setIsNew] = useState(false);
    const [copy, setCopy] = useState<VolumeDiscount | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [newLimit, setNewLimit] = useState<VolumeDiscountLimit>({ minimumQuantity: 0, discountPercent: 0 });

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const qgs = await api.getQuantityGroups();
            setQuantityGroups(qgs);
            if (volumeDiscountId === null) {
                setItem({ id: 0, name: '', quantityGroup: null, limits: [] });
                setIsNew(true);
                setIsEditing(true);
            } else {
                const data = await api.getVolumeDiscount(volumeDiscountId);
                data.limits = data.limits ?? [];
                setItem(data);
                setIsNew(false);
                setIsEditing(false);
            }
        } catch (err) {
            setError(String(err));
        } finally {
            setLoading(false);
        }
    }, [volumeDiscountId]);

    useEffect(() => { load(); }, [load]);

    if (loading) return <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>;
    if (error && !item) return <Alert severity="error">{error}</Alert>;
    if (!item) return null;

    const isQuantityGroupValid = !!item.quantityGroup && !!item.quantityGroup.id;
    // Legacy enforces quantity group only when creating; existing records can save freely.
    const canSave = !isNew || isQuantityGroupValid;

    const startEdit = () => { setCopy(JSON.parse(JSON.stringify(item))); setIsEditing(true); };
    const cancelEdit = () => {
        if (copy) setItem(copy);
        setIsEditing(false);
        setNewLimit({ minimumQuantity: 0, discountPercent: 0 });
    };

    const save = async () => {
        setSaving(true); setError(null);
        try {
            const response = isNew ? await api.createVolumeDiscount(item) : await api.updateVolumeDiscount(item);
            response.limits = response.limits ?? [];
            setItem(response);
            setIsNew(false);
            setIsEditing(false);
            setNewLimit({ minimumQuantity: 0, discountPercent: 0 });
        } catch (err) {
            setError(String(err));
        } finally {
            setSaving(false);
        }
    };

    const update = <K extends keyof VolumeDiscount>(field: K, value: VolumeDiscount[K]) => {
        setItem({ ...item, [field]: value });
    };

    const updateLimit = (index: number, patch: Partial<VolumeDiscountLimit>) => {
        update('limits', item.limits.map((l, i) => i === index ? { ...l, ...patch } : l));
    };
    const removeLimit = (index: number) => {
        update('limits', item.limits.filter((_, i) => i !== index));
    };
    const addLimit = () => {
        update('limits', [...item.limits, {
            minimumQuantity: Number(newLimit.minimumQuantity),
            discountPercent: Number(newLimit.discountPercent),
        }]);
        setNewLimit({ minimumQuantity: 0, discountPercent: 0 });
    };
    const clearLimits = () => {
        update('limits', []);
        setNewLimit({ minimumQuantity: 0, discountPercent: 0 });
    };

    const sortedLimits = isEditing
        ? item.limits.map((l, i) => ({ l, i }))
        : [...item.limits]
            .map((l, i) => ({ l, i }))
            .sort((a, b) => a.l.minimumQuantity - b.l.minimumQuantity);

    return (
        <Paper variant="outlined" sx={paperSx}>
            <DetailHeader
                title={item.name ?? ''}
                isEditing={isEditing}
                isNew={isNew}
                saving={saving}
                canSave={canSave}
                onEdit={startEdit}
                onSave={save}
                onCancel={cancelEdit}
            />

            <Box p={2}>
                <Stack spacing={2}>
                    {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

                    <FormRow label={t('volumeDiscount.name')}>
                        {!isEditing ? (
                            <Typography sx={sx14}>{item.name}</Typography>
                        ) : (
                            <TextField size="small" fullWidth
                                value={item.name ?? ''}
                                onChange={e => update('name', e.target.value)} />
                        )}
                    </FormRow>

                    <FormRow label={t('volumeDiscount.quantityGroup')}>
                        {!isEditing ? (
                            <Typography sx={sx14}>{item.quantityGroup?.name}</Typography>
                        ) : (
                            <FormControl size="small" fullWidth error={isNew && !isQuantityGroupValid}>
                                <Select
                                    value={item.quantityGroup?.id ?? ''}
                                    onChange={e => {
                                        const qg = quantityGroups.find(x => x.id === e.target.value);
                                        update('quantityGroup', qg ?? null);
                                    }}
                                >
                                    {[...quantityGroups]
                                        .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''))
                                        .map(qg => (
                                            <MenuItem key={qg.id} value={qg.id}>{qg.name}</MenuItem>
                                        ))}
                                </Select>
                            </FormControl>
                        )}
                    </FormRow>

                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ ...sx14, fontWeight: 'bold', textAlign: 'right' }}>
                                    {t('volumeDiscount.minimumQuantity')}
                                </TableCell>
                                <TableCell sx={{ ...sx14, fontWeight: 'bold', textAlign: 'right' }}>
                                    {t('volumeDiscount.discountPercent')}
                                </TableCell>
                                {isEditing && (
                                    <TableCell sx={{ textAlign: 'right' }}>
                                        <Button size="small" variant="contained" color="warning"
                                            onClick={clearLimits} disabled={item.limits.length === 0}>
                                            {t('common.clear')}
                                        </Button>
                                    </TableCell>
                                )}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {isEditing && (
                                <TableRow>
                                    <TableCell sx={{ textAlign: 'right' }}>
                                        <TextField size="small" type="number"
                                            value={newLimit.minimumQuantity}
                                            onChange={e => setNewLimit({ ...newLimit, minimumQuantity: e.target.value === '' ? 0 : Number(e.target.value) })}
                                            inputProps={{ style: { textAlign: 'right' } }} />
                                    </TableCell>
                                    <TableCell sx={{ textAlign: 'right' }}>
                                        <TextField size="small" type="number"
                                            value={newLimit.discountPercent}
                                            onChange={e => setNewLimit({ ...newLimit, discountPercent: e.target.value === '' ? 0 : Number(e.target.value) })}
                                            inputProps={{ style: { textAlign: 'right' } }} />
                                    </TableCell>
                                    <TableCell sx={{ textAlign: 'right' }}>
                                        <Button size="small" variant="contained" color="secondary" onClick={addLimit}>
                                            {t('common.add')}
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            )}

                            {sortedLimits.map(({ l, i }) => (
                                <TableRow key={i}>
                                    <TableCell sx={{ textAlign: 'right' }}>
                                        {!isEditing ? (
                                            <Typography sx={sx14}>{l.minimumQuantity.toFixed(2)}</Typography>
                                        ) : (
                                            <TextField size="small" type="number"
                                                value={l.minimumQuantity}
                                                onChange={e => updateLimit(i, { minimumQuantity: e.target.value === '' ? 0 : Number(e.target.value) })}
                                                inputProps={{ style: { textAlign: 'right' } }} />
                                        )}
                                    </TableCell>
                                    <TableCell sx={{ textAlign: 'right' }}>
                                        {!isEditing ? (
                                            <Typography sx={sx14}>{l.discountPercent.toFixed(2)}</Typography>
                                        ) : (
                                            <TextField size="small" type="number"
                                                value={l.discountPercent}
                                                onChange={e => updateLimit(i, { discountPercent: e.target.value === '' ? 0 : Number(e.target.value) })}
                                                inputProps={{ style: { textAlign: 'right' } }} />
                                        )}
                                    </TableCell>
                                    {isEditing && (
                                        <TableCell sx={{ textAlign: 'right' }}>
                                            <IconButton size="small" color="error" onClick={() => removeLimit(i)}>×</IconButton>
                                        </TableCell>
                                    )}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Stack>
            </Box>

            {saving && <Box display="flex" justifyContent="center" p={1}><CircularProgress size={20} /></Box>}
        </Paper>
    );
}
